import { LatLng } from "../utils/types";

// Default map centres per country: a desirable residential district in the main city,
// not the label centroid of the country, so the first map view lands where people live
const COUNTRY_COORDINATES: Record<string, LatLng> = {
  // Nordics
  'Finland': { lat: 60.16823797537535, lng: 24.936944610005185, },
  'Sweden': { lat: 59.33590, lng: 18.08100 }, // Östermalm, Stockholm
  'Norway': { lat: 59.92080, lng: 10.70450 }, // Frogner, Oslo
  'Denmark': { lat: 55.73000, lng: 12.57200 }, // Hellerup, Copenhagen
  'Iceland': { lat: 64.14300, lng: -21.92500 }, // Þingholt, Reykjavík
  // Baltics
  'Estonia': { lat: 59.43800, lng: 24.79300 }, // Kadriorg, Tallinn
  'Latvia': { lat: 57.00300, lng: 24.14700 }, // Mežaparks, Riga
  'Lithuania': { lat: 54.68800, lng: 25.25200 }, // Žvėrynas, Vilnius
  // Western Europe
  'United Kingdom': { lat: 51.49830, lng: -0.19380 }, // Kensington, London
  'Ireland': { lat: 53.32800, lng: -6.23000 }, // Ballsbridge, Dublin
  'France': { lat: 48.86230, lng: 2.27470 }, // 16e / Passy, Paris
  'Germany': { lat: 52.48700, lng: 13.26500 }, // Grunewald, Berlin
  'Netherlands': { lat: 52.35200, lng: 4.87300 }, // Oud-Zuid, Amsterdam
  'Belgium': { lat: 50.80200, lng: 4.34200 }, // Uccle / Ukkel, Brussels
  'Luxembourg': { lat: 49.60800, lng: 6.11200 }, // Belair, Luxembourg City
  'Austria': { lat: 48.24700, lng: 16.34200 }, // Döbling, Vienna
  'Switzerland': { lat: 47.37300, lng: 8.56600 }, // Zürichberg, Zurich
  // Southern Europe
  'Spain': { lat: 40.42950, lng: -3.68150 }, // Barrio de Salamanca, Madrid
  'Portugal': { lat: 38.71000, lng: -9.16300 }, // Lapa / Estrela, Lisbon
  'Italy': { lat: 41.92200, lng: 12.48700 }, // Parioli, Rome
  'Greece': { lat: 37.97900, lng: 23.74400 }, // Kolonaki, Athens
  'Malta': { lat: 35.91500, lng: 14.50300 }, // Sliema
  'Cyprus': { lat: 34.70500, lng: 33.10000 }, // Germasogeia, Limassol
  // Central and Eastern Europe
  'Poland': { lat: 52.19500, lng: 21.02400 }, // Stary Mokotów, Warsaw
  'Czech Republic': { lat: 50.07600, lng: 14.44300 }, // Vinohrady, Prague
  'Hungary': { lat: 47.52000, lng: 19.02800 }, // Rózsadomb, Budapest
  'Romania': { lat: 44.47000, lng: 26.09000 }, // Primăverii, Bucharest
  'Croatia': { lat: 45.81800, lng: 15.96400 }, // Pantovčak, Zagreb
  'Turkey': { lat: 41.04900, lng: 28.99400 }, // Nişantaşı, Istanbul
  'Russia': { lat: 55.74200, lng: 37.59600 }, // Ostozhenka, Moscow
  // Middle East and Africa
  'United Arab Emirates': { lat: 25.11200, lng: 55.13900 }, // Palm Jumeirah, Dubai
  'Israel': { lat: 32.09000, lng: 34.78000 }, // Old North, Tel Aviv
  'South Africa': { lat: -33.95000, lng: 18.37800 }, // Camps Bay, Cape Town
  // Asia-Pacific
  'Japan': { lat: 35.65200, lng: 139.72500 }, // Azabu / Hiroo, Tokyo
  'South Korea': { lat: 37.52700, lng: 127.03000 }, // Apgujeong, Gangnam, Seoul
  'China': { lat: 31.21200, lng: 121.44500 }, // Former French Concession, Shanghai
  'Hong Kong': { lat: 22.27100, lng: 114.14900 }, // The Peak
  'Singapore': { lat: 1.32500, lng: 103.81000 }, // Bukit Timah (D10)
  'Thailand': { lat: 13.73000, lng: 100.58200 }, // Thonglor, Bangkok
  'India': { lat: 18.95400, lng: 72.79700 }, // Malabar Hill, Mumbai
  'Australia': { lat: -33.87700, lng: 151.24300 }, // Double Bay, Sydney
  'New Zealand': { lat: -36.88300, lng: 174.80000 }, // Remuera, Auckland
  // Americas
  'United States': { lat: 40.77360, lng: -73.96500 }, // Upper East Side, New York
  'Canada': { lat: 43.68000, lng: -79.38300 }, // Rosedale, Toronto
  'Mexico': { lat: 19.43300, lng: -99.19300 }, // Polanco, Mexico City
  'Brazil': { lat: -23.56700, lng: -46.66800 }, // Jardins, São Paulo
  'Argentina': { lat: -34.58800, lng: -58.39300 }, // Recoleta, Buenos Aires
  'Chile': { lat: -33.40000, lng: -70.58500 }, // Vitacura, Santiago
};

// Map ISO country codes to country names
const COUNTRY_CODE_MAP: Record<string, string> = {
  'FI': 'Finland',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark',
  'IS': 'Iceland',
  'EE': 'Estonia',
  'LV': 'Latvia',
  'LT': 'Lithuania',
  'PL': 'Poland',
  'DE': 'Germany',
  'RU': 'Russia',
  'GB': 'United Kingdom',
  'UK': 'United Kingdom',
  'IE': 'Ireland',
  'NL': 'Netherlands',
  'BE': 'Belgium',
  'LU': 'Luxembourg',
  'FR': 'France',
  'ES': 'Spain',
  'IT': 'Italy',
  'PT': 'Portugal',
  'GR': 'Greece',
  'MT': 'Malta',
  'CY': 'Cyprus',
  'AT': 'Austria',
  'CH': 'Switzerland',
  'CZ': 'Czech Republic',
  'HU': 'Hungary',
  'RO': 'Romania',
  'HR': 'Croatia',
  'TR': 'Turkey',
  'AE': 'United Arab Emirates',
  'IL': 'Israel',
  'ZA': 'South Africa',
  'US': 'United States',
  'CA': 'Canada',
  'AU': 'Australia',
  'NZ': 'New Zealand',
  'JP': 'Japan',
  'KR': 'South Korea',
  'CN': 'China',
  'HK': 'Hong Kong',
  'SG': 'Singapore',
  'TH': 'Thailand',
  'IN': 'India',
  'BR': 'Brazil',
  'MX': 'Mexico',
  'AR': 'Argentina',
  'CL': 'Chile',
};

/**
 * Convert ISO country code to country name
 */
const getCountryNameFromCode = (countryCode: string): string => {
  return COUNTRY_CODE_MAP[countryCode.toUpperCase()] || countryCode;
};

/**
 * Get center coordinates for a given country name
 */
export const getCountryCoordinates = async (countryName: string): Promise<LatLng> => {
  // First, try to find in our static mapping
  if (COUNTRY_COORDINATES[countryName]) {
    return COUNTRY_COORDINATES[countryName];
  }

  // If not found in static mapping, try Mapbox geocoding as fallback
  try {
    const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(countryName)}.json?access_token=${accessToken}&types=country&limit=1`
    );

    const data = await response.json();

    if (data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      return { lat, lng };
    }
  } catch (error) {
    console.error('Error getting country coordinates from Mapbox:', error);
  }

  // Default to Finland if all else fails
  console.warn(`Could not find coordinates for country: ${countryName}, defaulting to Finland`);
  return COUNTRY_COORDINATES['Finland'];
};

/**
 * Coordinates of the newest sell listing in a country, or undefined when it has
 * none. The country default is empty ground abroad, so a visitor outside Finland
 * opens on real inventory instead. Finland returns before the fetch: it centres
 * as it always has, and must not pay a round trip for this.
 */
const getLatestSellListingCoordinates = async (countryName: string): Promise<LatLng | undefined> => {
  // An unmapped ISO code arrives here as the raw two letters, which no stored
  // address.country can equal, so asking would only cost a round trip.
  const isUnresolvedCode = countryName.length === 2 && countryName === countryName.toUpperCase();
  if (countryName === 'Finland' || isUnresolvedCode) {
    return undefined;
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/listings/latest-sell?country=${encodeURIComponent(countryName)}`
    );
    const data = await response.json();

    if (data && data.coordinates) {
      return { lat: data.coordinates.lat, lng: data.coordinates.lng };
    }
  } catch (error) {
    console.error('Error getting latest sell listing coordinates:', error);
  }

  return undefined;
};

export const getUserCountry = async (userCoordinates?: LatLng): Promise<string> => {
  try {
    if (!userCoordinates) {
      console.log('No user coordinates, using IP-based location');
      // If user denies permission or geolocation fails, use IP-based location
      const ipResponse = await fetch('https://ipapi.co/json/');
      const ipData = await ipResponse.json();
      if (ipData && ipData.country_name) {
        return ipData.country_name;
      }
      return 'Finland'; // Default to Finland if IP geolocation fails
    }
    const { lat, lng } = userCoordinates;

    // Use Mapbox's reverse geocoding to get the country
    const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${accessToken}&types=country`
    );

    const data = await response.json();

    if (data.features && data.features.length > 0) {
      // Extract country code from the result
      const countryCode = data.features[0].text || 'Finland'; // Default to Finland if not found
      console.log('Mapbox-based location:', countryCode);
      return countryCode;
    }

    return 'Finland'; // Default to Finland
  } catch (error) {
    console.error('Error getting user country:', error);
    return 'Finland'; // Default to Finland on error
  }
};

/**
 * Get user country and optionally return country coordinates when GPS is not available
 */
export const getUserCountryWithCoordinates = async (userCoordinates?: LatLng): Promise<{ country: string; coordinates?: LatLng }> => {
  try {
    /*     const isDev = process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'dev';
        if (isDev) {
          return { country: 'Finland', coordinates: COUNTRY_COORDINATES['Finland'] };
        } */
    if (!userCoordinates) {
      console.log('No user coordinates, using IP-based location from backend');
      // If user denies permission or geolocation fails, use backend IP-based location
      const ipResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/location`, {
        credentials: 'include' // Include cookies for better IP tracking
      });
      const ipData = await ipResponse.json();

      if (ipData && ipData.country) {
        // Map country code to country name if needed
        const countryName = getCountryNameFromCode(ipData.country);

        // Where Azure Maps gives a point it is the visitor's own city, so it wins;
        // the sell listing only replaces the country default, which is what abroad
        // actually falls back to.
        const coordinates = ipData.coordinates
          ? { lat: ipData.coordinates.lat, lng: ipData.coordinates.lng }
          : (await getLatestSellListingCoordinates(countryName)) ??
            (await getCountryCoordinates(countryName));

        return {
          country: countryName,
          coordinates: coordinates
        };
      }
      return {
        country: 'Finland',
        coordinates: COUNTRY_COORDINATES['Finland']
      };
    }

    const { lat, lng } = userCoordinates;

    // Use Mapbox's reverse geocoding to get the country
    const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${accessToken}&types=country`
    );

    const data = await response.json();

    if (data.features && data.features.length > 0) {
      // Extract country code from the result
      const countryCode = data.features[0].text || 'Finland';
      console.log('Mapbox-based location:', countryCode);
      return { country: countryCode };
    }

    return { country: 'Finland' };
  } catch (error) {
    console.error('Error getting user country:', error);
    return { country: 'Finland' };
  }
};